import {
  ZadCustomer,
  ZadCustomerAccountSummary,
  ZadCustomerPayment,
  ZadCustomerPaymentMethod,
  ZadCustomerPaymentStatus,
  ZadCustomerPaymentWritePayload,
  ZadSale,
  ZadSalePaymentSummary,
} from '../../../models/zad'
import {getZadCustomerDisplayName} from './customerHelpers'
import {ZadApiError, resolveZadUserMessage} from './errors'
import {parseZadPriceInput, roundZadMoney3} from './productHelpers'
import {getZadPrintableCustomerIdentity, getZadSaleCustomerId} from './saleHelpers'

export const ZAD_CUSTOMER_PAYMENT_STATUS_I18N: Record<ZadCustomerPaymentStatus, string> = {
  ACTIVE: 'ZAD.CUSTOMER_PAYMENTS.STATUS_ACTIVE',
  CANCELLED: 'ZAD.CUSTOMER_PAYMENTS.STATUS_CANCELLED',
}

export const ZAD_CUSTOMER_PAYMENT_METHOD_I18N: Record<ZadCustomerPaymentMethod, string> = {
  CASH: 'ZAD.CUSTOMER_PAYMENTS.METHOD_CASH',
  BANK_TRANSFER: 'ZAD.CUSTOMER_PAYMENTS.METHOD_BANK',
  CHEQUE: 'ZAD.CUSTOMER_PAYMENTS.METHOD_CHEQUE',
  CARD: 'ZAD.CUSTOMER_PAYMENTS.METHOD_CARD',
  OTHER: 'ZAD.CUSTOMER_PAYMENTS.METHOD_OTHER',
}

export const ZAD_CUSTOMER_PAYMENT_METHODS: ZadCustomerPaymentMethod[] = [
  'CASH',
  'BANK_TRANSFER',
  'CHEQUE',
  'CARD',
  'OTHER',
]

export type ZadCustomerPaymentEditorAllocation = {
  key: string
  sale: string
  saleNumber: string
  saleDate?: string
  saleTotal: number
  alreadyAllocated: number
  outstanding: number
  amount: string
}

export type ZadCustomerPaymentEditorValues = {
  customer: string
  paymentDate: string
  amount: string
  paymentMethod: ZadCustomerPaymentMethod
  reference: string
  notes: string
  allocations: ZadCustomerPaymentEditorAllocation[]
}

export type ZadCustomerPaymentPreview = {
  amount: number
  allocatedAmount: number
  unallocatedAmount: number
}

export type ZadCustomerOutstandingKind = 'due' | 'settled' | 'credit'
export type ZadSaleSettlementKind = 'unpaid' | 'partial' | 'settled' | 'excess'

export function todayZadIsoDate(): string {
  const today = new Date()
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate()
  ).padStart(2, '0')}`
}

export function parseZadCustomerPaymentAmount(
  value: string | number | null | undefined
): {ok: true; value: number} | {ok: false; errorId: string} {
  const parsed = parseZadPriceInput(value, {required: true})
  if (!parsed.ok) return {ok: false, errorId: 'ZAD.CUSTOMER_PAYMENTS.AMOUNT_REQUIRED'}
  if (parsed.value <= 0) return {ok: false, errorId: 'ZAD.CUSTOMER_PAYMENTS.AMOUNT_INVALID'}
  return {ok: true, value: parsed.value}
}

export function canCancelZadCustomerPayment(status?: ZadCustomerPaymentStatus | string): boolean {
  return status === 'ACTIVE'
}

export function canPrintZadCustomerPaymentReceipt(
  status?: ZadCustomerPaymentStatus | string
): boolean {
  return status === 'ACTIVE' || status === 'CANCELLED'
}

export function getZadCustomerPaymentReceiptCustomer(
  payment?: Partial<
    Pick<
      ZadCustomerPayment,
      | 'customer'
      | 'customerKind'
      | 'walkIn'
      | 'customerName'
      | 'walkInCustomerName'
      | 'context'
      | 'allocations'
    >
  > | null,
  walkInLabel = ''
): {
  displayName: string
  customerNumber: string
  phone: string
  taxId: string
  type: string
  isWalkIn: boolean
  walkInCustomerName: string
} {
  const empty = {
    displayName: walkInLabel,
    customerNumber: '',
    phone: '',
    taxId: '',
    type: '',
    isWalkIn: true,
    walkInCustomerName: '',
  }
  const printed = getZadPrintableCustomerIdentity(payment)
  if (printed.kind === 'WALK_IN') {
    return {
      ...empty,
      walkInCustomerName: printed.name || '',
      displayName: printed.name || walkInLabel,
    }
  }
  const customer = payment?.customer
  if (!customer) {
    return {...empty, isWalkIn: false, displayName: printed.name || ''}
  }
  if (typeof customer === 'string') {
    return {...empty, isWalkIn: false, displayName: printed.name || customer}
  }
  const type = String(customer.type || '').trim().toUpperCase()
  return {
    displayName: printed.name || getZadCustomerDisplayName(customer),
    customerNumber: String(customer.customerNumber || '').trim(),
    phone: String(customer.phone || '').trim(),
    taxId: type === 'ORGANIZATION' ? String(customer.taxId || '').trim() : '',
    type,
    isWalkIn: false,
    walkInCustomerName: '',
  }
}

export function emptyZadCustomerPaymentEditorValues(): ZadCustomerPaymentEditorValues {
  return {
    customer: '',
    paymentDate: todayZadIsoDate(),
    amount: '',
    paymentMethod: 'CASH',
    reference: '',
    notes: '',
    allocations: [],
  }
}

export function previewZadCustomerPayment(
  amountValue: string | number,
  allocations: Array<{amount: string | number}>
): ZadCustomerPaymentPreview | null {
  const amount = parseZadCustomerPaymentAmount(amountValue)
  if (!amount.ok) return null
  let allocated = 0
  for (const row of allocations) {
    const parsed = parseZadCustomerPaymentAmount(row.amount)
    if (!parsed.ok) return null
    allocated = roundZadMoney3(allocated + parsed.value)
  }
  return {
    amount: amount.value,
    allocatedAmount: allocated,
    unallocatedAmount: roundZadMoney3(amount.value - allocated),
  }
}

export function isDuplicateZadPaymentSale(
  allocations: Array<{sale: string}>,
  saleId: string
): boolean {
  return allocations.some((row) => row.sale && row.sale === saleId)
}

export function createZadCustomerPaymentAllocation(
  summary: ZadSalePaymentSummary,
  saleDate?: string
): ZadCustomerPaymentEditorAllocation {
  const suggested = summary.outstanding > 0 ? String(roundZadMoney3(summary.outstanding)) : ''
  return {
    key: `${summary.saleId}-${Date.now()}`,
    sale: summary.saleId,
    saleNumber: summary.saleNumber,
    saleDate,
    saleTotal: summary.total,
    alreadyAllocated: summary.allocated,
    outstanding: summary.outstanding,
    amount: suggested,
  }
}

export function validateZadCustomerPaymentForm(
  values: ZadCustomerPaymentEditorValues
): string | null {
  if (!String(values.customer || '').trim()) return 'ZAD.CUSTOMER_PAYMENTS.CUSTOMER_REQUIRED'
  if (!String(values.paymentDate || '').trim()) return 'ZAD.CUSTOMER_PAYMENTS.DATE_REQUIRED'
  const amount = parseZadCustomerPaymentAmount(values.amount)
  if (!amount.ok) return amount.errorId
  if (!values.paymentMethod) return 'ZAD.CUSTOMER_PAYMENTS.METHOD_REQUIRED'
  const seen = new Set<string>()
  let allocated = 0
  for (const row of values.allocations) {
    if (!row.sale) return 'ZAD.CUSTOMER_PAYMENTS.SALE_REQUIRED'
    if (seen.has(row.sale)) return 'ZAD.CUSTOMER_PAYMENTS.DUPLICATE_SALE'
    seen.add(row.sale)
    const alloc = parseZadCustomerPaymentAmount(row.amount)
    if (!alloc.ok) {
      return alloc.errorId === 'ZAD.CUSTOMER_PAYMENTS.AMOUNT_INVALID'
        ? 'ZAD.CUSTOMER_PAYMENTS.ALLOCATION_INVALID'
        : 'ZAD.CUSTOMER_PAYMENTS.ALLOCATION_REQUIRED'
    }
    if (alloc.value > row.outstanding) return 'ZAD.CUSTOMER_PAYMENTS.ALLOCATION_EXCEEDS'
    allocated = roundZadMoney3(allocated + alloc.value)
  }
  if (allocated > amount.value) return 'ZAD.CUSTOMER_PAYMENTS.ALLOCATIONS_EXCEED_PAYMENT'
  return null
}

export function sanitizeZadCustomerPaymentWritePayload(
  payload: ZadCustomerPaymentWritePayload
): ZadCustomerPaymentWritePayload {
  return {
    customer: String(payload.customer || '').trim(),
    paymentDate: String(payload.paymentDate || '').trim(),
    amount: Number(payload.amount) || 0,
    paymentMethod: payload.paymentMethod,
    reference: payload.reference != null ? String(payload.reference) : '',
    notes: String(payload.notes || '').trim(),
    allocations: (payload.allocations || []).map((row) => ({
      sale: String(row.sale || '').trim(),
      amount: Number(row.amount) || 0,
    })),
  }
}

export function toZadCustomerPaymentPayload(
  values: ZadCustomerPaymentEditorValues
): ZadCustomerPaymentWritePayload {
  const amount = parseZadCustomerPaymentAmount(values.amount)
  return {
    customer: String(values.customer || '').trim(),
    paymentDate: String(values.paymentDate || '').trim(),
    amount: amount.ok ? amount.value : 0,
    paymentMethod: values.paymentMethod,
    reference: String(values.reference ?? ''),
    notes: String(values.notes || '').trim(),
    allocations: values.allocations.map((row) => {
      const alloc = parseZadCustomerPaymentAmount(row.amount)
      return {
        sale: row.sale,
        amount: alloc.ok ? alloc.value : 0,
      }
    }),
  }
}

export function describeZadCustomerOutstanding(outstanding: number): {
  kind: ZadCustomerOutstandingKind
  amount: number
  i18nId: string
} {
  const value = roundZadMoney3(outstanding)
  if (value === 0) {
    return {kind: 'settled', amount: 0, i18nId: 'ZAD.CUSTOMER_PAYMENTS.SETTLED'}
  }
  if (value < 0) {
    return {
      kind: 'credit',
      amount: roundZadMoney3(Math.abs(value)),
      i18nId: 'ZAD.CUSTOMER_PAYMENTS.CUSTOMER_CREDIT',
    }
  }
  return {kind: 'due', amount: value, i18nId: 'ZAD.CUSTOMER_PAYMENTS.AMOUNT_DUE'}
}

export function describeZadSaleSettlement(
  allocated: number,
  outstanding: number
): {kind: ZadSaleSettlementKind; i18nId: string; amount: number} {
  const remaining = roundZadMoney3(outstanding)
  const paid = roundZadMoney3(allocated)
  if (remaining === 0) {
    return {kind: 'settled', amount: 0, i18nId: 'ZAD.CUSTOMER_PAYMENTS.SALE_SETTLED'}
  }
  if (remaining < 0) {
    return {
      kind: 'excess',
      amount: roundZadMoney3(Math.abs(remaining)),
      i18nId: 'ZAD.CUSTOMER_PAYMENTS.SALE_EXCESS',
    }
  }
  if (paid > 0 && remaining > 0) {
    return {kind: 'partial', amount: remaining, i18nId: 'ZAD.CUSTOMER_PAYMENTS.SALE_PARTIAL'}
  }
  return {kind: 'unpaid', amount: remaining, i18nId: 'ZAD.CUSTOMER_PAYMENTS.SALE_OUTSTANDING'}
}

export function formatZadCustomerPaymentCustomer(
  customer: ZadCustomerPayment['customer'] | ZadCustomer | string | null | undefined
): string {
  if (!customer) return ''
  if (typeof customer === 'string') return customer
  return getZadCustomerDisplayName(customer)
}

export function getZadCustomerPaymentCustomerId(
  customer: ZadCustomerPayment['customer'] | string | null | undefined
): string {
  if (!customer) return ''
  if (typeof customer === 'string') return customer
  return customer._id || ''
}

export function allocationSaleId(
  sale: ZadCustomerPayment['allocations'][number]['sale']
): string {
  if (!sale) return ''
  if (typeof sale === 'string') return sale
  return sale._id || ''
}

export function allocationSaleNumber(
  allocation: ZadCustomerPayment['allocations'][number]
): string {
  if (allocation.saleNumber) return allocation.saleNumber
  if (allocation.sale && typeof allocation.sale !== 'string') {
    return allocation.sale.saleNumber || '—'
  }
  return '—'
}

export function formatZadCustomerPickerLabel(
  customer?: Partial<ZadCustomer> | null
): string {
  if (!customer) return ''
  const number = customer.customerNumber || ''
  const name = getZadCustomerDisplayName(customer)
  const phone = customer.phone || ''
  return [number, name, phone].filter((part) => part && part !== '—').join(' · ')
}

export function buildZadCustomerPaymentNewPath(customerId?: string, saleId?: string): string {
  const params = new URLSearchParams()
  if (customerId) params.set('customer', customerId)
  if (saleId) params.set('sale', saleId)
  const query = params.toString()
  return query ? `/zad/customer-payments/new?${query}` : '/zad/customer-payments/new'
}

export function canCollectZadSalePayment(
  sale?: Pick<ZadSale, 'status' | 'customer'> | null,
  summary?: Pick<ZadSalePaymentSummary, 'outstanding'> | null
): boolean {
  if (!sale) return false
  if (sale.status !== 'VALIDATED') return false
  if (summary && Number(summary.outstanding) <= 0) return false
  return true
}

export function isRegisteredZadSaleCustomer(sale?: Pick<ZadSale, 'customer'> | null): boolean {
  return Boolean(getZadSaleCustomerId(sale?.customer))
}

export function resolveZadCustomerPaymentMessage(
  err: ZadApiError
): {i18nId: string; fallback: string} {
  const map: Record<string, string> = {
    VALIDATION_ERROR: 'ZAD.CUSTOMER_PAYMENTS.VALIDATION_ERROR',
    CUSTOMER_NOT_FOUND: 'ZAD.CUSTOMERS.NOT_FOUND',
    CUSTOMER_INACTIVE: 'ZAD.SALES.CUSTOMER_INACTIVE',
    SALE_NOT_FOUND: 'ZAD.SALES.NOT_FOUND',
    SALE_NOT_VALIDATED: 'ZAD.CUSTOMER_PAYMENTS.SALE_NOT_VALIDATED',
    SALE_HAS_NO_CUSTOMER: 'ZAD.CUSTOMER_PAYMENTS.SALE_HAS_NO_CUSTOMER',
    SALE_CUSTOMER_MISMATCH: 'ZAD.CUSTOMER_PAYMENTS.SALE_CUSTOMER_MISMATCH',
    SALE_OVERPAYMENT: 'ZAD.CUSTOMER_PAYMENTS.SALE_OVERPAYMENT',
    DUPLICATE_SALE_ALLOCATION: 'ZAD.CUSTOMER_PAYMENTS.DUPLICATE_SALE',
    INVALID_PAYMENT_AMOUNT: 'ZAD.CUSTOMER_PAYMENTS.AMOUNT_INVALID',
    INVALID_ALLOCATION_AMOUNT: 'ZAD.CUSTOMER_PAYMENTS.ALLOCATION_INVALID',
    PAYMENT_NOT_FOUND: 'ZAD.CUSTOMER_PAYMENTS.NOT_FOUND',
    PAYMENT_ALREADY_CANCELLED: 'ZAD.CUSTOMER_PAYMENTS.ALREADY_CANCELLED',
    INSUFFICIENT_TENDER: 'ZAD.POS.INSUFFICIENT_TENDER',
    WALK_IN_PAYMENT_REQUIRES_SALE: 'ZAD.POS.WALK_IN_INVALID',
    WALK_IN_UNALLOCATED_FORBIDDEN: 'ZAD.POS.WALK_IN_INVALID',
    INVALID_WALK_IN_PAYMENT: 'ZAD.POS.WALK_IN_INVALID',
    COLLECT_OPERATION_CONFLICT: 'ZAD.POS.COLLECT_CONFLICT',
    IDEMPOTENCY_CONFLICT: 'ZAD.POS.COLLECT_CONFLICT',
    ROLE_FORBIDDEN: 'ZAD.COMMON.FORBIDDEN',
  }
  if (err.errorCode && map[err.errorCode]) {
    return {i18nId: map[err.errorCode], fallback: err.error}
  }
  return resolveZadUserMessage(err)
}

export function customerSummaryUsesSignedOutstanding(
  summary: ZadCustomerAccountSummary
): boolean {
  return typeof summary.outstanding === 'number' && typeof summary.activePaymentsTotal === 'number'
}
